import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, type PostValue } from './client'

/**
 * ADM -> Changes, calea noua. Perechile vechi, din `application/controllers/Adm.php`:
 *
 *   changes($scope)     -> GET  /adm/changes[/{scope}]
 *   changesData($scope) -> GET  /adm/changes[/{scope}]/table
 *   authorizeChange()   -> POST /adm/changes/authorize
 *
 * Calea NU poarta `/api/v2`: il pune `apiGet`/`apiPost`.
 *
 * UN ECRAN, CINCI USI. Ingustarea sta in ADRESA, nu in date - la fel ca in
 * portal - deci `/adm/changes/hardware` nu poate arata programe, si legatura se
 * poate da mai departe cuiva. Din aceeasi usa vine si numele modulului scris in
 * coltul din stanga sus: cine intra din Inventoree nu citeste "Softsee".
 *
 * Cifra de sus, filtrele si drepturile vin de la SERVER (`shell`). Ecranul nu
 * hotaraste singur ce are voie omul: `canWrite` spune doar daca sa deseneze
 * butonul, iar refuzul insusi e pe server.
 */

/** Ingustarile pe care le cunoaste ecranul - `Changes::SCOPES`. */
export const CHANGE_SCOPES = ['software', 'hardware', 'proactivitee', 'opteemum'] as const

export type ChangeScope = (typeof CHANGE_SCOPES)[number]

export function isChangeScope(value: string): value is ChangeScope {
  return (CHANGE_SCOPES as readonly string[]).includes(value)
}

/** O optiune de filtru, asa cum o trimite serverul. */
export interface Option {
  value: string
  label: string
}

/** Un rand de LEGATURA - fila "Between items". */
export interface ChangeLink {
  ID: number
  detected_at: string
  change: number
  sourceCiId: number
  sourceName: string
  targetCiId: number
  targetName: string
  relationName: string
  companyName: string
  observations: number
  confidence: number
  lastSeen: string
  notified: number
}

export interface ChangesShell {
  scope: string
  /** Modulul din coltul din stanga sus - si culoarea barii cu el. */
  module: string
  pageTitle: string
  pageHeader: string
  breadcrumbs: string[]
  windows: number[]
  filters: { days: number; company_id: number; relay_device_id: number; run_type: number; state: number }
  types: Option[]
  severities: Option[]
  softwareKinds: Option[]
  softwareClasses: Option[]
  /** Cate n-a autorizat nimeni, numarate cu ACELEASI filtre ca lista. */
  unauthorized: number
  canWrite: boolean
  links: ChangeLink[]
  linkKinds: Option[]
}

/** Un rand de tabel, cu tot ce a socotit serverul pe el. */
export interface ChangeRow {
  ID: number
  DT_RowId: string
  detected_at: string
  change_type: number
  field_key: string | null
  new_value: string | null
  old_value: string | null
  new_value_text: string
  old_value_text: string
  authorized: number
  authorized_by_ticket_id: number
  window_name: string | null
  source: number
  source_label: string
  source_message: string
  ci_id: number
  ci_name: string | null
  company_name: string | null
  type_label: string
  severity: string
  severity_label: string
  subject_name: string
  software_kind: string
  software_kind_label: string
  software_class: string
  software_class_label: string
}

/** Cate randuri cad pe fiecare optiune - vin ODATA cu tabelul. */
export interface FilterCounts {
  severity: Record<string, number>
  authorized: Record<string, number>
  types: Record<string, number>
}

export interface ChangesTablePage {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  data: ChangeRow[]
  filterCounts: FilterCounts
}

/** Ce trimite ecranul cand cere lista. */
export interface ChangesTableRequest {
  draw: number
  start: number
  length: number
  orderColumn: number
  orderDir: 'asc' | 'desc'
  search: string
  days: number
  /** '' inseamna "tot", si e altceva decat "cele autorizate". */
  authorized: string
  severity: string
  type: string
  software_kind: string
  software_class: string
}

/** Bucata de adresa a ingustarii: goala pentru ecranul general. */
function scopePath(scope: string): string {
  return isChangeScope(scope) ? `/${scope}` : ''
}

export function changesShellQuery(scope: string, days: number) {
  return queryOptions({
    queryKey: ['adm', 'changes', 'shell', scope, days],
    queryFn: () => apiGet<ChangesShell>(`/adm/changes${scopePath(scope)}?days=${days}`),
  })
}

export function changesTableQuery(scope: string, r: ChangesTableRequest) {
  const params = new URLSearchParams({
    draw: String(r.draw),
    start: String(r.start),
    length: String(r.length),
    orderColumn: String(r.orderColumn),
    orderDir: r.orderDir,
    search: r.search,
    days: String(r.days),
    authorized: r.authorized,
    severity: r.severity,
    type: r.type,
    software_kind: r.software_kind,
    software_class: r.software_class,
  })

  return queryOptions({
    queryKey: ['adm', 'changes', 'table', scope, r],
    queryFn: () => apiGet<ChangesTablePage>(`/adm/changes${scopePath(scope)}/table?${params.toString()}`),
  })
}

/** Scrie ca schimbarea a fost, pana la urma, asteptata. */
export function authorizeChange(id: number, ticketId: number) {
  const body: Record<string, PostValue> = { id, ticket_id: ticketId }

  return apiPost<{ status: string; message: string }>('/adm/changes/authorize', body)
}
